import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinkClass = (active, activeStyle = 'yellow', isMobile = false) => {
  const baseClasses = isMobile
    ? 'block w-full text-left font-bold uppercase tracking-wider py-3 px-4 rounded transition-all text-sm'
    : 'px-3 py-1.5 rounded transition-all font-bold uppercase tracking-wider text-xs md:text-sm';

  if (!active) {
    return `${baseClasses} text-slate-400 hover:text-white hover:bg-white/5`;
  }
  if (activeStyle === 'orange') {
    return `${baseClasses} text-white bg-urgent-orange/80 shadow-[0_0_10px_rgba(255,87,34,0.3)] border border-urgent-orange/20`;
  }
  if (activeStyle === 'cyan') {
    return `${baseClasses} text-white bg-accent-cyan/60 shadow-[0_0_10px_rgba(0,240,255,0.3)] border border-accent-cyan/20`;
  }
  if (activeStyle === 'purple') {
    return `${baseClasses} text-white bg-accent-purple/60 shadow-[0_0_10px_rgba(142,45,226,0.3)] border border-accent-purple/20`;
  }
  return `${baseClasses} text-black bg-banana-yellow shadow-[0_0_10px_rgba(245,224,29,0.3)]`;
};

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login';

  // Automatically close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { label: 'Home', path: '/', style: 'yellow' },
    { label: 'I Want to Help', path: '/help', style: 'yellow' },
    { label: 'Get Help', path: '/get-help', style: 'orange' },
    { label: 'Report Camp', path: '/report-camp', style: 'cyan' },
    { label: 'Donate', path: '/donate', style: 'purple' },
    { label: 'Privacy', path: '/privacy', style: 'yellow' },
  ];

  if (location.pathname.startsWith('/admin')) {
    navItems.push({ label: 'Dashboard', path: '/admin', style: 'yellow' });
  }

  return (
    <header className="bg-charcoal-dark border-b border-white/5 text-white py-4 md:py-6 px-4 md:px-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-accent-purple/10 to-accent-cyan/10 opacity-30 pointer-events-none" />
      <div className="max-w-6xl mx-auto relative z-10">
        {!isAdminPage && (
          <div className="absolute top-1 right-3 md:top-2 md:right-6">
            <Link
              to="/login"
              className="text-xs text-slate-400 hover:text-banana-yellow underline transition-colors"
            >
              Admin Login
            </Link>
          </div>
        )}

        <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 pt-4 md:pt-2">
          <Link
            to="/"
            className="flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-banana-yellow rounded"
            aria-label="Tent Finder home"
          >
            <img
              src="/logo.png"
              alt="Not By Chance Inc. Logo"
              className="w-16 sm:w-20 md:w-28 h-auto drop-shadow-[0_0_15px_rgba(245,224,29,0.2)]"
            />
          </Link>
          <div className="text-center md:text-left">
            <Link
              to="/"
              className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-banana-yellow rounded"
            >
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold uppercase tracking-wide leading-none mb-1 hover:text-banana-yellow transition-colors font-display">
                Tent Finder
              </h1>
            </Link>
            <p className="text-xs sm:text-sm md:text-base text-slate-400 max-w-4xl leading-relaxed">
              Not By Chance Inc. – Providing emergency support to homeless and underserved
              individuals in Louisville and Shepherdsville, KY.
            </p>
          </div>
        </div>

        {/* Mobile Menu Toggle Button */}
        <div className="mt-4 md:hidden flex justify-center border-t border-white/5 pt-3">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center gap-2 bg-charcoal-card border border-white/10 px-4 py-2 rounded text-xs font-bold uppercase tracking-wider text-banana-yellow hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-banana-yellow"
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-navigation-mobile"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <svg
              className="w-4 h-4 fill-current"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {mobileMenuOpen ? (
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              ) : (
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
              )}
            </svg>
            <span>{mobileMenuOpen ? 'Close Menu' : 'Navigation Menu'}</span>
          </button>
        </div>

        {/* Desktop Primary Navigation */}
        <nav
          className="hidden md:flex mt-6 pt-4 border-t border-white/5 items-center justify-center gap-2 md:gap-4"
          aria-label="Primary Desktop"
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={navLinkClass(location.pathname === item.path, item.style, false)}
              aria-current={location.pathname === item.path ? 'page' : undefined}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Dropdown Navigation */}
        {mobileMenuOpen && (
          <nav
            id="primary-navigation-mobile"
            className="md:hidden mt-3 p-3 bg-charcoal-card/95 border border-white/10 rounded-lg space-y-1 shadow-2xl animate-fadeIn"
            aria-label="Primary Mobile"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={navLinkClass(location.pathname === item.path, item.style, true)}
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
